<template>
    <a-modal
        :visible="true"
        width="80vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="pom-wrap"
        @cancel="$emit('close')"
    >
        <div class="pom-modal">
            <div class="pom-head">
                <div>
                    <h3>Pedidos do fornecedor</h3>
                    <div class="pom-sub">{{ provider.name }}</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="pom-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="pom-tools">
                <a-select
                    v-model="selectedStore"
                    class="pom-search"
                    show-search
                    allowClear
                    placeholder="Loja"
                    :options="storeOptions"
                    :filterOption="filterOption"
                    @change="loadPage(1)"
                />
            </div>

            <div class="pom-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="orders"
                    :row-key="(record, index) => record.id_pedido || index"
                    :loading="loading"
                    :pagination="pagination"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText: 'Nenhum pedido encontrado para este fornecedor.' }"
                    @change="(p) => loadPage(p.current)"
                >
                    <template slot="pedido" slot-scope="text, order">
                        <div class="pom-strong">#{{ order.id_pedido }}</div>
                        <div class="pom-muted">Cotação {{ order.id_cotacao }}</div>
                    </template>
                    <template slot="loja" slot-scope="text, order">
                        <div>{{ order.loj_descricao }}</div>
                        <div class="pom-muted">{{ formatDate(order.data_geracao) }}</div>
                    </template>
                    <template slot="vendedor" slot-scope="text, order">
                        <div class="pom-strong">{{ order.fornecedor ? order.fornecedor.for_nome : "--" }}</div>
                        <div>{{ order.vendedor }}</div>
                    </template>
                    <template slot="valor" slot-scope="text, order">
                        <span class="pom-value">{{ order.valor_pedido | currency }}</span>
                    </template>
                    <template slot="status" slot-scope="text, order">
                        <a-tag :color="order.status === 'C' ? 'green' : 'red'">
                            {{ order.status === "C" ? "Confirmado" : "Aguardando confirmação" }}
                        </a-tag>
                        <div class="pom-muted">{{ formatDate(order.data_confirmacao) }}</div>
                    </template>
                    <template slot="nota" slot-scope="text, order">
                        {{ order.nota ? order.nota : "--" }}
                    </template>
                    <template slot="acoes" slot-scope="text, order">
                        <ActionMenu :actions="getActions(order)" />
                    </template>
                </a-table>
            </div>

            <div class="pom-foot">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>

        <cc-loader-full v-if="printing" />
        <OrderPartial v-if="order_data" :order_data="order_data" />
    </a-modal>
</template>

<script>
import moment from "moment";
import OrderService from "@/services/OrderService.js";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import OrderPartial from "@/components/partials/_print_order.partial";

export default {
    name: "ProviderOrdersV2Modal",
    components: { ActionMenu, OrderPartial },
    props: {
        provider: { type: Object, required: true },
    },
    data() {
        return {
            svc: new OrderService(),
            loading: false,
            printing: false,
            orders: [],
            stores: [],
            selectedStore: undefined,
            order_data: null,
            pagination: {
                current: 1,
                pageSize: 50,
                total: 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            columns: [
                { title: "Pedido / Cotação", key: "pedido", scopedSlots: { customRender: "pedido" } },
                { title: "Loja / Data", key: "loja", scopedSlots: { customRender: "loja" } },
                { title: "Fornecedor / Vendedor", key: "vendedor", scopedSlots: { customRender: "vendedor" } },
                { title: "Valor", key: "valor", align: "right", scopedSlots: { customRender: "valor" } },
                { title: "Status / Data Confirmação", key: "status", align: "center", scopedSlots: { customRender: "status" } },
                { title: "Avaliação", key: "nota", align: "center", scopedSlots: { customRender: "nota" } },
                { title: "Ações", key: "acoes", align: "center", fixed: "right", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    computed: {
        storeOptions() {
            return this.stores.map((s) => ({ value: s.loj_id, label: s.loj_descricao }));
        },
    },
    created() {
        this.svc
            .getStores()
            .then(({ data }) => {
                this.stores = data.requisitantes || [];
            })
            .catch(() => {});
        this.loadPage(1);
    },
    methods: {
        filterOption(input, option) {
            const label = option.componentOptions.children[0].text || "";
            return label.toLowerCase().includes(input.toLowerCase());
        },
        formatDate(date) {
            return date ? moment(String(date)).format("DD/MM/YYYY") : "--";
        },
        loadPage(page) {
            this.loading = true;
            this.svc
                .findOrdersByQuotationId(undefined, this.provider.id, this.selectedStore, page)
                .then(({ data }) => {
                    this.orders = data.pedidos.data || [];
                    this.pagination.total = data.pedidos.total || 0;
                    this.pagination.current = page;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getActions(order) {
            return [
                { label: "Imprimir pedido", icon: "picture_as_pdf", iconClass: "action-danger", onClick: () => this.print(order) },
                {
                    label: "Ver detalhes",
                    icon: "error_outline",
                    iconClass: "action-info",
                    onClick: () => this.openDetails(order.order_id ? "subrequest" : "order-info", order),
                },
            ];
        },
        detailsRoute(name, order) {
            if (name === "subrequest") {
                return {
                    name,
                    params: {
                        return_to: "monitors-page",
                        id: order.id_cotacao,
                        seller_id: order.usu_id,
                        store_id: order.id_loja,
                        tab: "pedidos",
                        provider_id: order.for_id,
                    },
                };
            }
            return {
                name,
                params: {
                    backRouteName: "monitors-page",
                    id: order.id_cotacao,
                    sellerid: order.usu_id,
                    sid: order.id_loja,
                    providerid: order.for_id,
                },
                query: { backRoute: 2 },
            };
        },
        openDetails(name, order) {
            const route = this.detailsRoute(name, order);
            if (this.isRunningInWebView()) {
                this.$router.push(route);
                return;
            }
            window.open(this.$router.resolve(route).href, "_blank");
        },
        isRunningInWebView() {
            const ua = navigator.userAgent;
            return (
                /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua) ||
                /; wv\).*Chrome\//.test(ua) ||
                /Android.*Version\/\d\.\d\s/.test(ua) ||
                ua.includes("WebView") ||
                Object.prototype.hasOwnProperty.call(window, "flutter_inappwebview") ||
                !!(window.webkit && window.webkit.messageHandlers)
            );
        },
        print(order) {
            this.printing = true;
            this.svc
                .get_order_data({
                    id_pedido: [order.id_pedido],
                    id_cotacao: [order.id_cotacao],
                    id_loja: [order.id_loja],
                    usu_id: [order.usu_id],
                })
                .then(({ data }) => {
                    this.order_data = data.data;
                    this.$nextTick(() => {
                        const originalTitle = document.title;
                        document.title = `pedido-${order.id_pedido}.pdf`;
                        this.$htmlToPaper("print-order", { name: "pedido.pdf" }, () => {
                            this.order_data = null;
                            document.title = originalTitle;
                        });
                    });
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.printing = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.pom-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.pom-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.pom-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.pom-x.ant-btn,
.pom-x.ant-btn:focus,
.pom-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.pom-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.pom-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.pom-search {
    flex: 1 1 240px;
    max-width: 360px;
}
.pom-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.pom-strong {
    font-weight: 600;
    color: #262626;
}
.pom-muted {
    font-size: 12px;
    color: #8c8c8c;
}
.pom-value {
    white-space: nowrap;
    font-weight: 600;
    color: #30aa4c;
}
.pom-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.pom-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
